import { Wizard, WizardProps } from "@amzn/awsui-components-react";
import React, { useEffect, useState } from "react";
import {
  AppLayout,
  Container,
  Header,
  SpaceBetween,
  Modal,
  Alert,
  Box,
  Button,
} from "@amzn/awsui-components-react/polaris";
import { getProgramsNamesForUser } from "../app/api";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../app/rootReducer";
import {
  publishStamp as publishStampConnector,
  publishWeblab as publishWeblabConnector,
  publishStampAndWeblab as publishStampAndWeblabConnector,
  setStamperWizardTouched,
} from "./stamperWizardConnector";
import { Breadcrumbs } from "./Breadcrumbs";
import { trackButtonClick, trackComponentView } from "../telemetry/telemetry";
import { createNotificationDefinition } from "../notification/notificationConnector";
import { StamperOptions } from "./StamperOptions";
import Review from "./Review";
import { AsinMarketplaceSelectionLinks } from "./StamperTable";
import { AsinMarketplaceWeblab } from "./WeblabTable";
import { StamperASINImport } from "./StamperASINImport";
import { fetchEditableAttributes } from "../porAsinList/porAsinListSlice";
import { AuthorizedAttribute } from "../../server/brass/model";

export type OptionDefinition = {
  label?: string;
  value?: string;
};

export interface AsinWithListOfMarketplaces {
  asin: string;
  marketplaces: string[];
}

export function StamperWizard() {
  // const dispatch = useDispatch();
  const {
    asinsWithListOfMarketplaces,
    asinsMarketplaceLiveness,
    asinMarketplaceSelectionLinks,
    asinMarketplaceWeblab,
    touched,
    stampSelected,
    weblabSelected,
    program,
    programName,
    partiness,
    type,
  } = useSelector((state: RootState) => state.stamperWizard);

  const dispatch = useDispatch();
  const { editableAttributes, isGettingEditableAttributes } = useSelector(
    (state: RootState) => state.porAsinList
  );
  const [programs, setPrograms] = useState<OptionDefinition[] | undefined>();
  const [asinMarketplacesValid, setAsinMarketplacesValid] =
    useState<boolean>(false);
  const [noStamperToolOptionSelected, setNoStamperToolOptionSelected] =
    useState<boolean>(true);
  const [stamperMissingAttributes, setStamperMissingAttributes] =
    useState<boolean>(true);
  const [weblabMissingAttributes, setWeblabMissingAttributes] =
    useState<boolean>(true);
  const [cancelModalVisible, setCancelModalVisible] = React.useState(false);

  useEffect(() => {
    if (!isGettingEditableAttributes) {
      dispatch(fetchEditableAttributes());
    }
  });
  useEffect(() => {
    if (programs === undefined || programs.length == 0) {
      getProgramsNamesForUser().then((programs) =>
        setPrograms(
          (programs || []).map((program) => ({
            label: program.name,
            value: program.id,
          }))
        )
      );
    }
  });

  useEffect(() => {
    trackComponentView("stamper", "stamper");
  }, []);

  useEffect(() => {
    asinMarketplacesSelected(asinsWithListOfMarketplaces);
  }, [asinsWithListOfMarketplaces]);

  useEffect(() => {
    setNoStamperToolOptionSelected(!weblabSelected && !stampSelected);
    setStamperMissingAttributes(
      stampSelected && (program == null || partiness == null || type == null)
    );
    setWeblabMissingAttributes(weblabSelected && program == null);
  }, [weblabSelected, stampSelected, program, partiness, type]);

  function userIsAuthorized(editableAttributes: AuthorizedAttribute[]) {
    return editableAttributes.some(
      (attribute) =>
        (attribute.attributeName === "publish_stamp" && attribute.authorized) ||
        (attribute.attributeName === "publish_weblab" && attribute.authorized)
    );
  }
  const participation_participation =
    !program || !partiness || !type
      ? undefined
      : `c2l-v1-${program}|${partiness}|${type}`;

  const asinMarketplacesSelected = (
    asinsWithListOfMarketplaces: AsinWithListOfMarketplaces[]
  ) => {
    if (
      asinsWithListOfMarketplaces.length == 0 ||
      asinsWithListOfMarketplaces.some(
        (asinWithListOfMarketplaces) =>
          asinWithListOfMarketplaces.marketplaces.length == 0
      )
    ) {
      setAsinMarketplacesValid(false);
    } else {
      setAsinMarketplacesValid(true);
    }
  };

  function validateNextStep(detail: WizardProps.NavigateDetail): boolean {
    const step = detail.requestedStepIndex;
    if (activeStepIndex > step) {
      return true;
    }
    switch (step) {
      case 1:
        dispatch(setStamperWizardTouched({ item: "1", value: true }));
        return asinMarketplacesValid;
      case 2:
        dispatch(setStamperWizardTouched({ item: "2", value: true }));
        return (
          asinMarketplacesValid &&
          !(
            noStamperToolOptionSelected ||
            weblabMissingAttributes ||
            stamperMissingAttributes
          )
        );
    }
    return true;
  }

  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);

  const weblabCheck = (asinMpWeblab: AsinMarketplaceWeblab) =>
    asinMpWeblab.liveness == "error" ||
    asinMpWeblab.liveness == "" ||
    (asinMpWeblab.liveness != "not_live" && !asinMpWeblab.reasonForWeblab);

  function validateAttributesForApiHasIssues(): boolean {
    const weblabHasIssues = asinMarketplaceWeblab.some(weblabCheck);
    const stamperHasIssues = false;
    return (
      (weblabSelected && weblabHasIssues) || (stampSelected && stamperHasIssues)
    );
  }

  const publishStamp = (
    asinMarketplaceSelectionLinks: AsinMarketplaceSelectionLinks[],
    program_participation: string
  ) => {
    return new Promise<void>((resolve) => {
      dispatch(
        publishStampConnector(
          asinMarketplaceSelectionLinks,
          program_participation
        )
      );
      resolve();
    });
  };

  const publishWeblab = (
    asinMarketplaceWeblab: AsinMarketplaceWeblab[],
    programName: string
  ) => {
    return new Promise<void>((resolve) => {
      dispatch(publishWeblabConnector(asinMarketplaceWeblab, programName));
      resolve();
    });
  };
  const publishStampAndPublishWeblab = (
    asinMarketplaceSelectionLinks: AsinMarketplaceSelectionLinks[],
    asinMarketplaceWeblab: AsinMarketplaceWeblab[],
    program_participation: string,
    programName: string
  ) => {
    return new Promise<void>((resolve) => {
      dispatch(
        publishStampAndWeblabConnector(
          asinMarketplaceSelectionLinks,
          asinMarketplaceWeblab,
          program_participation,
          programName
        )
      );
      resolve();
    });
  };
  const returnHome = () => {
    window.location.href = "/";
  };

  return !userIsAuthorized(editableAttributes) ? (
    <Alert data-testid="stamper-unauthorized-alert" statusIconAriaLabel="Info">
      You do not have permissions. Please reguest permissions.
    </Alert>
  ) : (
    <div data-testid="stamper-wizard-wrapper">
      <Modal
        data-testid="stamper-cancel-modal"
        onDismiss={() => setCancelModalVisible(false)}
        visible={cancelModalVisible}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                data-testid="stamper-cancel-go-back-button"
                variant="link"
                onClick={() => setCancelModalVisible(false)}
              >
                Go Back
              </Button>
              <Button
                data-testid="stamper-cancel-confirm-button"
                variant="primary"
                onClick={returnHome}
              >
                Yes (all changes will be lost)
              </Button>
            </SpaceBetween>
          </Box>
        }
        header="Cancel Edit"
      >
        <Alert statusIconAriaLabel="Warning" type="warning">
          Are you sure that you want to cancel edit? The changes that you made
          won't be saved.
        </Alert>
      </Modal>
      <AppLayout
        data-testid="stamper-app-layout"
        breadcrumbs={Breadcrumbs()}
        navigationHide={true}
        toolsHide={true}
        stickyNotifications={true}
        content={
          <div style={{ maxWidth: "1200px" }}>
            <Header variant={"h1"}>Stamper Tool</Header>
            <Wizard
              data-testid="stamper-wizard"
              i18nStrings={{
                stepNumberLabel: (stepNumber) => `Step ${stepNumber}`,
                navigationAriaLabel: "Steps",
                cancelButton: "Cancel",
                previousButton: "Previous",
                nextButton: "Next",
                submitButton: "Submit",
                optional: "optional",
              }}
              isLoadingNextStep={programs === undefined}
              allowSkipTo={false}
              onNavigate={({ detail }) => {
                if (validateNextStep(detail)) {
                  setActiveStepIndex(detail.requestedStepIndex);
                }
              }}
              activeStepIndex={activeStepIndex}
              onCancel={() => setCancelModalVisible(true)}
              onSubmit={() => {
                dispatch(setStamperWizardTouched({ item: "3", value: true }));
                if (!validateAttributesForApiHasIssues()) {
                  if (weblabSelected && stampSelected) {
                    trackButtonClick(
                      "stamper",
                      "publishStampAndPublishWeblab",
                      {
                        asinMarketplaceSelectionLinks,
                        asinMarketplaceWeblab,
                        participationParticipation:
                          participation_participation || "",
                        programName: programName || "",
                      }
                    );
                    publishStampAndPublishWeblab(
                      asinMarketplaceSelectionLinks,
                      asinMarketplaceWeblab,
                      participation_participation || "",
                      programName || ""
                    );
                  } else if (weblabSelected) {
                    trackButtonClick("stamper", "publishWeblab", {
                      asinMarketplaceWeblab,
                      programName: programName || "",
                    });
                    publishWeblab(asinMarketplaceWeblab, programName || "");
                  } else if (stampSelected) {
                    trackButtonClick("stamper", "publishStamp", {
                      asinMarketplaceSelectionLinks,
                      participationParticipation:
                        participation_participation || "",
                    });
                    publishStamp(
                      asinMarketplaceSelectionLinks,
                      participation_participation || ""
                    );
                  }
                } else {
                  dispatch(
                    createNotificationDefinition(
                      "error",
                      "Required Fields Missing",
                      true,
                      `We cannot submit request as a required value or field is missing. Please fix all error messages on page before trying again`
                    )
                  );
                }
              }}
              steps={[
                {
                  title: "ASIN Setup",
                  content: (
                    <StamperASINImport
                      dispatch={dispatch}
                      touched={touched}
                      asinsWithListOfMarketplaces={asinsWithListOfMarketplaces}
                    />
                  ),
                },
                {
                  title: "Select Stamper Tool Options",
                  content: (
                    <Container>
                      <SpaceBetween direction="vertical" size="l">
                        <StamperOptions
                          dispatch={dispatch}
                          touched={touched}
                          weblabSelected={weblabSelected}
                          stampSelected={stampSelected}
                          programs={programs || []}
                          program={program}
                          programName={programName}
                          partiness={partiness}
                          type={type}
                        />
                      </SpaceBetween>
                    </Container>
                  ),
                  isOptional: false,
                },
                {
                  title: "Review and Submit",
                  content: (
                    <Review
                      dispatch={dispatch}
                      asinsMarketplaceLiveness={asinsMarketplaceLiveness}
                      asinsWithListOfMarketplaces={asinsWithListOfMarketplaces}
                      weblabSelected={weblabSelected}
                      stampSelected={stampSelected}
                      programs={programs || []}
                      program={program}
                      programName={programName}
                      partiness={partiness}
                      type={type}
                      setActiveStepIndex={setActiveStepIndex}
                    />
                  ),
                },
              ]}
            />
          </div>
        }
      />
    </div>
  );
}

export default StamperWizard;
